import CustomAxios from "./CustomAxios";

// Cache for AppConfig by inventory ID
const appConfigCache = {};
const appConfigPromises = {}; // Track ongoing requests to avoid duplicate calls

/**
 * Get hub ID from hostname by matching hub_link
 * @returns {Promise<number|null>} The inventory ID or null if not found
 */
async function getHubIdFromHostname() {
  try {
    const hostname = window.location.hostname;
    
    // Extract subdomain and domain from hostname
    // e.g., "keev.example.com" -> subdomain: "keev", domain: "example.com"
    const hostnameParts = hostname.split(".");
    
    if (hostnameParts.length < 3) {
      return null; // Need at least subdomain.domain.tld
    }
    
    const subdomain = hostnameParts[0];
    const domain = hostnameParts.slice(-2).join("."); // Get last 2 parts for domain
    
    if (!subdomain) {
      return null;
    }
    
    // Build the expected hub_link from subdomain and domain
    const expectedHubLink = `https://${subdomain}.${domain}`;
    
    // Fetch inventory list and find matching hub_link
    // Wrap in try-catch to prevent redirect loops if API fails
    let response;
    try {
      response = await CustomAxios.get("inventory/view");
    } catch (apiError) {
      // If API call fails (e.g., 401, 500), don't let it cause a redirect loop
      // Just return null and let the app continue with localStorage hubId
      console.warn("[getHubIdFromHostname] API call failed, skipping hostname lookup:", apiError?.response?.status || apiError?.message);
      return null;
    }
    
    const inventoryList = response?.data || [];
    
    const matchingHub = inventoryList.find(
      (hub) => hub.hub_link === expectedHubLink
    );
    
    if (matchingHub && matchingHub.id) {
      console.log("[getHubIdFromHostname] Found hub:", matchingHub.id, "for hostname:", hostname);
      return matchingHub.id;
    }
    
    return null;
  } catch (error) {
    // Catch any other errors and prevent them from causing issues
    console.error("[getHubIdFromHostname] Error:", error);
    return null;
  }
}

/**
 * Get the current hub/inventory ID from localStorage or hostname
 * @returns {number|null} The inventory ID or null if not found
 */
export function getCurrentHubId() {
  try {
    const selectedHub = localStorage.getItem("selected_hub");
    if (!selectedHub || selectedHub === "" || selectedHub === "[]") {
      return null;
    }

    // Try parsing as JSON first
    try {
      const parsed = JSON.parse(selectedHub);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return typeof parsed[0] === 'number' ? parsed[0] : parseInt(parsed[0], 10);
      }
      if (typeof parsed === 'number') {
        return parsed;
      }
      if (typeof parsed === 'string') {
        const num = parseInt(parsed, 10);
        return isNaN(num) ? null : num;
      }
    } catch (e) {
      // Not JSON, try as number
      const num = parseInt(selectedHub, 10);
      return isNaN(num) ? null : num;
    }
  } catch (error) {
    console.error("Error parsing selected_hub:", error);
    return null;
  }
  return null;
}

/**
 * Fetch AppConfig from backend for a given inventory ID
 * @param {number} inventoryId - The inventory/hub ID
 * @returns {Promise<Object|null>} The AppConfig object or null if not found
 */
async function fetchAppConfig(inventoryId) {
  if (!inventoryId) {
    return null;
  }

  // Return cached data if available
  if (appConfigCache[inventoryId]) {
    return appConfigCache[inventoryId];
  }

  // If there's already a pending request, wait for it
  if (appConfigPromises[inventoryId]) {
    return appConfigPromises[inventoryId];
  }

  // Create new request
  const promise = CustomAxios.get(`app_config/?inventory=${inventoryId}&is_active=true`)
    .then((response) => {
      const appConfigs = response?.data || [];
      const appConfig = Array.isArray(appConfigs) && appConfigs.length > 0 ? appConfigs[0] : null;
      
      if (appConfig) {
        appConfigCache[inventoryId] = appConfig;
      }
      
      delete appConfigPromises[inventoryId];
      return appConfig;
    })
    .catch((error) => {
      console.error("Error fetching AppConfig:", error);
      delete appConfigPromises[inventoryId];
      return null;
    });

  appConfigPromises[inventoryId] = promise;
  return promise;
}

/**
 * Get AppConfig for the current hub (synchronous getter, returns cached or null)
 * @returns {Object|null} The AppConfig object or null
 */
export function getAppConfigSync() {
  const hubId = getCurrentHubId();
  if (!hubId) {
    return null;
  }
  return appConfigCache[hubId] || null;
}

/**
 * Get AppConfig for the current hub (async, fetches if not cached)
 * @returns {Promise<Object|null>} The AppConfig object or null
 */
export async function getAppConfig() {
  const hubId = getCurrentHubId();
  if (!hubId) {
    return null;
  }
  return await fetchAppConfig(hubId);
}

/**
 * Clear the AppConfig cache (useful when AppConfig is updated)
 * @param {number|null} inventoryId - If provided, clears only that inventory's cache. Otherwise clears all.
 */
export function clearAppConfigCache(inventoryId = null) {
  if (inventoryId) {
    delete appConfigCache[inventoryId];
    delete appConfigPromises[inventoryId];
  } else {
    Object.keys(appConfigCache).forEach((key) => {
      delete appConfigCache[key];
    });
    Object.keys(appConfigPromises).forEach((key) => {
      delete appConfigPromises[key];
    });
  }
}

/**
 * Prefetch AppConfig for a given inventory ID
 * @param {number} inventoryId - The inventory/hub ID
 */
export async function prefetchAppConfig(inventoryId) {
  if (inventoryId) {
    await fetchAppConfig(inventoryId);
  }
}

/**
 * Get hub ID from subdomain by matching place or hub_link
 * @param {string} subdomain - The subdomain (e.g., "nilgris")
 * @returns {Promise<number|null>} The inventory ID or null if not found
 */
export async function getHubIdFromSubdomain(subdomain) {
  if (!subdomain) {
    return null;
  }

  try {
    const response = await CustomAxios.get("inventory/view");
    const inventoryList = response?.data || [];
    const subdomainLower = subdomain.toLowerCase();

    // Try to match by place or hub_link containing subdomain
    const matchingHub = inventoryList.find((hub) => {
      const hubLink = (hub.hub_link || "").toLowerCase();
      const place = (hub.place || "").toLowerCase();
      return hubLink.includes(subdomainLower) || place.includes(subdomainLower);
    });

    if (matchingHub && matchingHub.id) {
      return matchingHub.id;
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Fetch AppConfig by hub ID (public access via whitelist)
 * @param {number} inventoryId - The inventory/hub ID
 * @returns {Promise<Object|null>} The AppConfig object or null if not found
 */
export async function fetchAppConfigPublic(inventoryId) {
  if (!inventoryId) {
    return null;
  }

  try {
    const url = `app_config/?inventory=${inventoryId}&is_active=true`;
    const response = await CustomAxios.get(url);
    const appConfigs = response?.data || [];
    const appConfig = Array.isArray(appConfigs) && appConfigs.length > 0 ? appConfigs[0] : null;
    
    if (appConfig) {
      // Cache it for later use
      appConfigCache[inventoryId] = appConfig;
    }
    
    return appConfig;
  } catch (error) {
    return null;
  }
}

/**
 * Initialize hub from hostname if not already set in localStorage
 * This is used when accessing via hub link (e.g., keev.arivu-iq.com, keev.6tharivu.com, or any custom domain)
 * @returns {Promise<number|null>} The hub ID if found and set, null otherwise
 */
export async function initializeHubFromHostname() {
  // Check if hub is already set
  const currentHubId = getCurrentHubId();
  if (currentHubId) {
    return currentHubId;
  }
  
  // Only try to get hub ID from hostname if user is authenticated
  // This prevents API calls before login and stops the reload loop
  const access_token = localStorage.getItem('authtoken');
  const refresh_token = localStorage.getItem('refreshToken');
  
  if (!access_token || !refresh_token) {
    console.log("[initializeHubFromHostname] User not authenticated, skipping hostname lookup");
    return null;
  }
  
  // Try to get hub ID from hostname
  const hubId = await getHubIdFromHostname();
  if (hubId) {
    // Set in localStorage so subsequent calls work
    localStorage.setItem("selected_hub", hubId.toString());
    console.log("[initializeHubFromHostname] Set hub ID:", hubId, "from hostname");
    return hubId;
  }
  
  return null;
}

